import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import * as FileSaver from 'file-saver';
import { SearchableSelectComponent, SearchableSelectOption } from '../../../components/searchable-select/searchable-select.component';
import { ReturnsService } from '../../../services/returns.service';
import { NotificationService } from '../../../services/notification.service';

/**
 * Selezione confermata dal popup: intervallo di date (yyyy-MM-dd) e codici
 * vettore. Con "Tutti i vettori" l'elenco è vuoto: il backend include tutti i vettori.
 */
export interface PlatformExcelSelection {
  dateFrom: string;
  dateTo: string;
  carrierCodes: string[];
}

type PlatformExcelDateField = 'dateFrom' | 'dateTo';

/**
 * Popup "Resi per vettore" della pagina /returns.
 *
 * Chiede un intervallo di date (due flatpickr, da/a) e uno o più vettori
 * (select multipla). Con "Genera" chiama GET /pallets/returns-report con le due
 * date (yyyy-MM-dd) e i codici vettore (`carrierCodes`, vuoto = tutti),
 * scarica l'xlsx restituito e notifica il padre con l'evento `saved`.
 * L'elenco dei vettori arriva da GET /anagraphics/carriers.
 */
@Component({
  selector: 'app-platform-excel-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, SearchableSelectComponent],
  templateUrl: './platform-excel-popup.component.html',
  styleUrl: './platform-excel-popup.component.css'
})
export class PlatformExcelPopupComponent implements OnChanges, OnDestroy {

  @Input() visible: boolean = false;

  @Output() closed = new EventEmitter<void>();
  /** Report generato e scaricato: selezione usata per la chiamata */
  @Output() saved = new EventEmitter<PlatformExcelSelection>();

  @ViewChild('dateFromInput') dateFromInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dateToInput') dateToInput?: ElementRef<HTMLInputElement>;

  /** Date scelte nei picker, in formato yyyy-MM-dd (stringa vuota se non valorizzate) */
  dateFrom = '';
  dateTo = '';
  /** Codici dei vettori selezionati (vuoto = tutti i vettori) */
  selectedCarriers: string[] = [];

  /** Opzioni della select vettori */
  carrierOptions: SearchableSelectOption[] = [];
  isLoadingCarriers = false;
  /** True durante la generazione e il download del report */
  isSubmitting = false;
  errorMessage = '';

  private pickers = new Map<PlatformExcelDateField, FlatpickrInstance>();
  /** True dopo il primo caricamento riuscito: i vettori non vengono richiesti di nuovo */
  private carriersLoaded = false;

  constructor(private returnsService: ReturnsService,
              private notificationService: NotificationService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      if (this.visible) {
        this.resetState();
        this.loadCarriers();
        // Gli input esistono solo dopo il render dell'*ngIf: i picker vanno creati al tick successivo
        setTimeout(() => this.initPickers());
      } else {
        this.destroyPickers();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyPickers();
  }

  // ─── VETTORI ───

  private loadCarriers(): void {
    if (this.carriersLoaded) {
      return;
    }
    this.isLoadingCarriers = true;
    this.returnsService.getCarriers().subscribe({
      next: (carriers) => {
        this.carrierOptions = (carriers || [])
          .filter((c) => !!c.carrierCode)
          .map((c) => ({
            value: c.carrierCode,
            label: c.carrierName && c.carrierName !== c.carrierCode
              ? `${c.carrierName} (${c.carrierCode})`
              : c.carrierCode
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'it'));
        this.carriersLoaded = true;
        this.isLoadingCarriers = false;
      },
      error: (error) => {
        this.isLoadingCarriers = false;
        // Il report resta generabile per tutti i vettori anche senza la tendina
        this.errorMessage = 'Impossibile caricare l\'elenco dei vettori';
        console.error('Errore caricamento vettori:', error);
      }
    });
  }

  // ─── DATE ───

  /** Flatpickr a data singola, stesso formato dei filtri data della pagina resi */
  private initPickers(): void {
    this.destroyPickers();
    this.createPicker('dateFrom', this.dateFromInput?.nativeElement);
    this.createPicker('dateTo', this.dateToInput?.nativeElement);
  }

  private createPicker(field: PlatformExcelDateField, el?: HTMLInputElement): void {
    if (!el) {
      return;
    }
    const picker = flatpickr(el, {
      dateFormat: 'd/m/Y',
      allowInput: false,
      locale: Italian,
      defaultDate: this[field] || undefined,
      onChange: (dates: Date[]) => {
        this[field] = dates.length ? this.toIsoDay(dates[0]) : '';
        this.errorMessage = '';
      }
    });
    this.pickers.set(field, picker);
  }

  private destroyPickers(): void {
    this.pickers.forEach((picker) => picker.destroy());
    this.pickers.clear();
  }

  openPicker(field: PlatformExcelDateField): void {
    this.pickers.get(field)?.open();
  }

  clearDate(field: PlatformExcelDateField): void {
    this[field] = '';
    this.pickers.get(field)?.clear();
    this.errorMessage = '';
  }

  /** Imposta l'intervallo agli ultimi N giorni (oggi compreso) */
  setLastDays(days: number): void {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (days - 1));
    this.dateFrom = this.toIsoDay(from);
    this.dateTo = this.toIsoDay(to);
    this.pickers.get('dateFrom')?.setDate(from, false);
    this.pickers.get('dateTo')?.setDate(to, false);
    this.errorMessage = '';
  }

  /** Data locale come yyyy-MM-dd, senza passare da UTC */
  private toIsoDay(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  // ─── STATO E CONFERMA ───

  private resetState(): void {
    this.dateFrom = '';
    this.dateTo = '';
    this.selectedCarriers = [];
    this.isSubmitting = false;
    this.errorMessage = '';
  }

  get isRangeInvalid(): boolean {
    return !!this.dateFrom && !!this.dateTo && this.dateFrom > this.dateTo;
  }

  /** True quando non è selezionato nessun vettore: il report include tutti i vettori */
  get isAllCarriers(): boolean {
    return this.selectedCarriers.length === 0;
  }

  get canSave(): boolean {
    return !!this.dateFrom && !!this.dateTo && !this.isRangeInvalid
      && !this.isSubmitting;
  }

  onCarriersChange(): void {
    this.errorMessage = '';
  }

  onClose(): void {
    if (this.isSubmitting) {
      return;
    }
    this.closed.emit();
  }

  onSave(): void {
    if (!this.dateFrom || !this.dateTo) {
      this.errorMessage = 'Seleziona sia la data di inizio che quella di fine';
      return;
    }
    if (this.isRangeInvalid) {
      this.errorMessage = 'La data di inizio non può essere successiva a quella di fine';
      return;
    }

    const selection: PlatformExcelSelection = {
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      carrierCodes: [...this.selectedCarriers]
    };

    this.isSubmitting = true;
    this.errorMessage = '';

    this.returnsService.generateReturnsReport(selection.dateFrom, selection.dateTo, selection.carrierCodes)
      .subscribe({
        next: (blob) => {
          this.isSubmitting = false;
          FileSaver.saveAs(blob, this.buildFileName(selection));
          this.notificationService.success(this.describeSuccess(selection));
          this.saved.emit(selection);
          this.closed.emit();
        },
        error: (error) => {
          this.isSubmitting = false;
          this.describeError(error).then((message) => this.errorMessage = message);
        }
      });
  }

  private describeSuccess(selection: PlatformExcelSelection): string {
    const count = selection.carrierCodes.length;
    if (count === 0) {
      return 'Report generato per tutti i vettori';
    }
    return count === 1 ? 'Report generato per 1 vettore' : `Report generato per ${count} vettori`;
  }

  /** Nome del file scaricato: resi-vettori_<da>_<a>.xlsx */
  private buildFileName(selection: PlatformExcelSelection): string {
    return `resi-vettori_${selection.dateFrom}_${selection.dateTo}.xlsx`;
  }

  /**
   * Messaggio leggibile dall'errore HTTP. La risposta è richiesta come blob,
   * quindi un eventuale messaggio del backend va letto dal blob stesso.
   */
  private async describeError(error: any): Promise<string> {
    const body = error?.error;
    if (body instanceof Blob) {
      try {
        const text = await body.text();
        const parsed = JSON.parse(text);
        const message = parsed?.message || parsed?.error;
        if (message) {
          return message;
        }
      } catch {
        // Corpo non JSON: si usa il messaggio in base allo status
      }
    } else if (typeof body === 'string' && body) {
      return body;
    } else if (body?.message) {
      return body.message;
    }
    switch (error?.status) {
      case 400: return 'Parametri non validi: controlla date e vettori';
      case 401: return 'Non autorizzato';
      case 403: return 'Non hai i permessi per generare il report';
      case 404: return 'Nessun reso trovato per i vettori e le date indicate';
      case 0: return 'Impossibile contattare il server';
      default: return 'Errore durante la generazione del report';
    }
  }
}
